import PushSheet from '@/components/PushSheet';
import { useT } from '@/i18n';
import { useRestaurantStore } from '@/stores/restaurant.store';
import {
  AssistantRuntimeProvider,
  AuiConfig,
  Tools,
  useAui,
  useAuiState,
} from '@assistant-ui/react';
import { Link as F7Link, Toolbar, f7 } from 'framework7-react';
import { useState } from 'react';
import { useAssistantRuntime } from '../runtime/useAssistantRuntime';
import { useAiChatStore } from '../stores/ai-chat.store';
import { assistantToolkit } from '../tools/toolkit';
import { AssistantThread } from './AssistantThread';
import { ThreadListPanel } from './ThreadListPanel';

const config = AuiConfig({ tools: Tools({ toolkit: assistantToolkit }) });

function PanelToolbar({
  onOpenList,
  onRename,
}: {
  onOpenList: () => void;
  onRename: (id: string) => void;
}) {
  const t = useT().aiAssistant;
  const aui = useAui();
  const mainId = useAuiState((s) => s.threads.mainThreadId);
  const hasRemote = useAuiState((s) => s.threadListItem.remoteId !== undefined);

  const openThreadActions = () => {
    f7.actions
      .create({
        buttons: [
          [
            { text: t.rename, onClick: () => onRename(mainId) },
            { text: t.archive, onClick: () => void aui.threads.item({ id: mainId }).archive() },
            {
              text: t.delete,
              color: 'red',
              onClick: () => void aui.threads.item({ id: mainId }).delete(),
            },
            { text: t.cancel },
          ],
        ],
      })
      .open();
  };

  return (
    <Toolbar>
      <div className="left">
        <F7Link href={false} iconF7="xmark" sheetClose data-testid="ai-close" />
      </div>
      <div className="title">{t.title}</div>
      <div className="right">
        {hasRemote && (
          <F7Link
            href={false}
            iconF7="ellipsis_circle"
            aria-label={t.threadActions}
            data-testid="ai-thread-actions"
            onClick={openThreadActions}
          />
        )}
        <F7Link
          href={false}
          iconF7="chat_bubble_2"
          aria-label={t.conversations}
          data-testid="ai-open-threads"
          onClick={onOpenList}
        />
        <F7Link
          href={false}
          iconF7="square_pencil"
          aria-label={t.newChat}
          data-testid="ai-new-chat"
          onClick={() => aui.threads.switchToNewThread()}
        />
      </div>
    </Toolbar>
  );
}

function AssistantPanel({ restaurantId }: { restaurantId: string }) {
  const runtime = useAssistantRuntime(restaurantId);
  const [listOpen, setListOpen] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);

  return (
    <AssistantRuntimeProvider runtime={runtime} config={config}>
      <div className="ai-panel">
        <PanelToolbar
          onOpenList={() => setListOpen(true)}
          onRename={(id) => {
            setRenamingId(id);
            setListOpen(true);
          }}
        />
        <div className="ai-panel-body">
          <AssistantThread />
        </div>
        <ThreadListPanel
          open={listOpen}
          renamingId={renamingId}
          onRenamingChange={setRenamingId}
          onClose={() => setListOpen(false)}
        />
      </div>
    </AssistantRuntimeProvider>
  );
}

export function AssistantSheet() {
  const isOpen = useAiChatStore((s) => s.isOpen);
  const close = useAiChatStore((s) => s.close);
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);

  return (
    <PushSheet
      open={isOpen}
      onClose={close}
      className="sheet-side-panel"
      style={{ height: '92vh' }}
      swipeToClose
    >
      {restaurantId && <AssistantPanel key={restaurantId} restaurantId={restaurantId} />}
    </PushSheet>
  );
}
